// =============================================================================
// components/KpiIncidents/IncidentTable.jsx
//
// Dense, professional table. Severity shown as a coloured bar on the left
// of the row (no wide chip). Compact typography. Selected incident's row
// is highlighted.
//
// FIX5 — back to CLIENT-SIDE pagination. `rows` is now the FULL (filtered,
// maxRows-capped) result set from the server, not just one page of it — see
// IncidentDashboard/useIncidents. The DataGrid pages that in memory itself,
// so there's no more rowCount/paginationModel/onPaginationModelChange wired
// to the server; just a default page size.
//
// FIX (kept): "Failed to execute 'setPointerCapture' on 'Element':
// InvalidStateError" when resizing columns (easier to reproduce with an
// empty table). This is a known issue in @mui/x-data-grid: when a prop the
// DataGrid does not expect to change reference on every render
// (rowSelectionModel, getRowId, onRowClick, getRowClassName — all recreated
// here as inline literals/functions) changes mid-resize-drag, the grid can
// re-render the column separator mid-gesture and lose the reference to the
// element that held the pointer capture. It does not always happen; it is
// more likely with an empty grid because the "no rows" overlay makes the
// grid re-render more often. Stabilising these props with useMemo/useCallback
// reduces the odds considerably — but if it persists, it is worth checking
// the installed @mui/x-data-grid version (this bug has fixes in more recent
// releases).
// =============================================================================
import { useCallback, useMemo } from 'react';
import { Chip, Box, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { TECH_COLOR, SEVERITY_OPTIONS } from '../../constants/kpiConstants';

const SEV_MAP = SEVERITY_OPTIONS.reduce(function (acc, s) {
	acc[s.value] = s;
	return acc;
}, {});

function fmtNum(value, decimals) {
	if (decimals === undefined) decimals = 2;
	if (value == null) return '—';
	const abs = Math.abs(value);
	if (abs >= 1e9) return (value / 1e9).toFixed(1) + 'B';
	if (abs >= 1e6) return (value / 1e6).toFixed(1) + 'M';
	if (abs >= 1e3) return (value / 1e3).toFixed(1) + 'K';
	return value.toFixed(decimals);
}

function fmtDate(d) {
	if (!d) return '—';
	try {
		return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
	} catch (e) { return '—'; }
}

// Outside the component — never changes reference.
function getRowId(row) { return row.incidentId; }

export default function IncidentTable({ rows, loading, selected, onRowClick }) {

	const columns = useMemo(function () { return [
		{
			field: 'tech',
			headerName: 'Tech',
			width: 52,
			sortable: true,
			renderCell: function (params) {
				return (
					<Box sx={{
						bgcolor: (TECH_COLOR[params.value] || '#94a3b8') + '1f',
						color:   TECH_COLOR[params.value] || '#94a3b8',
						fontWeight: 700, fontSize: '0.7rem',
						px: 0.75, py: 0.25, borderRadius: 0.75,
						minWidth: 32, textAlign: 'center',
					}}>
						{params.value}
					</Box>
				);
			},
		},
		{
			field: 'kpiName',
			headerName: 'KPI',
			flex: 1.5,
			minWidth: 160,
			renderCell: function (params) {
				return (
					<Typography variant="body2" sx={{
						fontSize: '0.82rem', fontWeight: 500,
						overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
					}}>
						{params.value}
					</Typography>
				);
			},
		},
		{
			field: 'vendor',
			headerName: 'Vendor',
			width: 80,
			renderCell: function (params) {
				return (
					<Typography variant="body2" sx={{ fontSize: '0.78rem' }} noWrap>
						{params.value}
					</Typography>
				);
			},
		},
		{
			field: 'siteId',
			headerName: 'Site',
			width: 100,
			renderCell: function (params) {
				return (
					<Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
						{params.value}
					</Typography>
				);
			},
		},
		{
			field: 'cellId',
			headerName: 'Cell',
			width: 110,
			renderCell: function (params) {
				return (
					<Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.76rem', color: 'text.secondary' }}>
						{params.value}
					</Typography>
				);
			},
		},
		{
			field: 'kpiValue',
			headerName: 'Value',
			width: 68,
			type: 'number',
			renderCell: function (params) {
				return (
					<Typography variant="body2" sx={{
						fontFamily: 'monospace', fontSize: '0.82rem',
						fontWeight: 600, color: '#dc2626',
					}}>
						{fmtNum(params.value, 2)}
					</Typography>
				);
			},
		},
		{
			field: 'triggerValue',
			headerName: 'Trigger',
			width: 68,
			type: 'number',
			description: 'Value that triggered the alarm (P95/P5/SLA/Avg)',
			renderCell: function (params) {
				return (
					<Typography variant="body2" sx={{
						fontFamily: 'monospace', fontSize: '0.78rem', color: 'text.secondary',
					}}>
						{fmtNum(params.value, 2)}
					</Typography>
				);
			},
		},
		{
			field: 'volume',
			headerName: 'Vol',
			width: 65,
			type: 'number',
			renderCell: function (params) {
				return (
					<Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
						{fmtNum(params.value, 0)}
					</Typography>
				);
			},
		},
		{
			field: 'durationDays',
			headerName: 'Dur',
			width: 58,
			type: 'number',
			renderCell: function (params) {
				const v = params.value || 0;
				let color = '#64748b';
				if (v >= 7) color = '#dc2626';
				else if (v >= 3) color = '#ea580c';
				return (
					<Typography variant="body2" sx={{
						fontSize: '0.78rem', color: color, fontWeight: v >= 3 ? 600 : 400,
					}}>
						{v}d
					</Typography>
				);
			},
		},
		{
			field: 'startDate',
			headerName: 'Opened',
			width: 80,
			renderCell: function (params) {
				return (
					<Typography variant="body2" sx={{ fontSize: '0.76rem', color: 'text.secondary' }}>
						{fmtDate(params.value)}
					</Typography>
				);
			},
		},
		{
			field: 'status',
			headerName: 'Status',
			width: 75,
			renderCell: function (params) {
				const isOpen = params.value === 'Open';
				return (
					<Chip
						label={isOpen ? 'Open' : 'Closed'}
						size="small"
						sx={{
							height: 20, fontSize: '0.68rem', fontWeight: 600,
							bgcolor: isOpen ? '#fee2e2' : '#dcfce7',
							color:   isOpen ? '#991b1b' : '#166534',
							border: 'none',
						}}
					/>
				);
			},
		},
	]; }, []);

	// --- Stabilised props (same reference across renders, except when the ---
	// --- value they depend on actually changes) — see note at top of file.
	const rowSelectionModel = useMemo(
		function () { return selected?.incidentId ? [selected.incidentId] : []; },
		[selected?.incidentId],
	);

	const handleRowClick = useCallback(
		function (params) { onRowClick(params.row); },
		[onRowClick],
	);

	const getRowClassName = useCallback(
		function (params) { return 'sev-' + (params.row.severity || 3); },
		[],
	);

	const localeText = useMemo(function () {
		return {
			noRowsLabel: 'No incidents for the current filters',
			footerRowSelected: function (count) { return count + ' selected'; },
		};
	}, []);

	return (
		<Box sx={{ height: '100%', width: '100%' }}>
			<DataGrid
				rows={rows}
				columns={columns}
				loading={loading}
				getRowId={getRowId}
				rowSelectionModel={rowSelectionModel}
				onRowClick={handleRowClick}
				pageSizeOptions={[25, 50, 100]}
				initialState={{
					pagination: { paginationModel: { pageSize: 25 } },
					sorting: { sortModel: [] },
				}}
				density="compact"
				disableColumnMenu
				getRowClassName={getRowClassName}
				sx={{
					border: 0,
					'& .MuiDataGrid-columnHeaders': {
						backgroundColor: '#fafbfc',
						borderBottom: '1px solid #e5e7eb',
						'& .MuiDataGrid-columnHeaderTitle': {
							fontSize: '0.72rem', fontWeight: 700,
							textTransform: 'uppercase', letterSpacing: 0.4,
							color: '#475569',
						},
					},
					'& .MuiDataGrid-row': {
						borderLeft: '3px solid transparent',
						transition: 'background-color 0.1s',
					},
					'& .MuiDataGrid-row.sev-1': { borderLeftColor: '#dc2626' },
					'& .MuiDataGrid-row.sev-2': { borderLeftColor: '#ea580c' },
					'& .MuiDataGrid-row.sev-3': { borderLeftColor: '#0891b2' },
					'& .MuiDataGrid-row:hover': {
						cursor: 'pointer',
						backgroundColor: '#f8fafc',
					},
					'& .MuiDataGrid-row.Mui-selected': {
						backgroundColor: '#eff6ff !important',
						'&:hover': { backgroundColor: '#dbeafe !important' },
					},
					'& .MuiDataGrid-cell': {
						borderBottom: '1px solid #f1f5f9',
					},
					'& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
					'& .MuiDataGrid-footerContainer': {
						borderTop: '1px solid #e5e7eb',
						minHeight: 42,
					},
				}}
				localeText={localeText}
			/>
		</Box>
	);
}